import { useRef, useState } from "react";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { IconBadge } from "@/shared/components/ui/IconBadge";
import { ListSearch } from "@/shared/components/ui/ListSearch";
import { Section } from "@/shared/components/ui/Section";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import {
  LucideBell,
  LucideCalendar,
  LucideClipboardList,
  LucideClock,
  LucideGlobe,
  LucideInfo,
  LucideLink,
  LucideMessagesSquare,
  LucideMail,
  LucideSearch,
  LucideUserRound,
  LucideUsers,
} from "lucide-react";

const groups = ["Your business", "Stay in touch", "Find your way"] as const;

export function AppsList() {
  const navigation = useAppNavigation();
  const { user } = useAuth();
  const { business } = useCurrentBusiness();
  const [query, setQuery] = useState("");
  const searchContainer = useRef<HTMLDivElement>(null);

  const destinations = [
    { group: "Your business", title: "Profile", description: "Your profile, business and location details", icon: LucideUserRound, onClick: navigation.goToProfile },
    { group: "Your business", title: "Customers", description: "Contact details and visit history", icon: LucideUsers, onClick: navigation.goToClients },
    { group: "Your business", title: "Appointments", description: "Your calendar and booking details", icon: LucideCalendar, onClick: navigation.goToAppointments },
    { group: "Your business", title: "Appointment rules", description: "Customer cancellation, rescheduling and booking notice", icon: LucideClipboardList, onClick: navigation.goToAppointmentRules },
    { group: "Your business", title: "Services", description: "Prices, durations and service details", icon: LucideClipboardList, onClick: navigation.goToServices },
    { group: "Your business", title: "Forms", description: "Preview forms and attach them to services", icon: LucideClipboardList, onClick: navigation.goToForms },
    { group: "Your business", title: "Working hours", description: "Availability and weekly time intervals", icon: LucideClock, onClick: navigation.goToAvailabilities },
    ...(user && business?.ownerId === user.id ? [{ group: "Your business", title: "Stripe", description: "Set up your US business Stripe account", icon: LucideLink, onClick: navigation.goToStripe }] : []),
    { group: "Your business", title: "Connected apps", description: "Review and disconnect account access", icon: LucideLink, onClick: navigation.goToConnectedApps },
    { group: "Stay in touch", title: "Messages", description: "Existing customer conversations", icon: LucideMessagesSquare, onClick: navigation.goToConversations },
    { group: "Stay in touch", title: "Notifications", description: "Booking updates and cancellation receipts", icon: LucideBell, onClick: navigation.goToNotifications },
    { group: "Stay in touch", title: "Reminders", description: "Appointment reminders and customer emails", icon: LucideMail, onClick: navigation.goToCustomerEmails },
    { group: "Find your way", title: "Search", description: "Find customers, appointments and services", icon: LucideSearch, onClick: navigation.goToSearch },
    {
      group: "Find your way",
      title: "Website",
      description: "Your logo, description and booking link",
      icon: LucideGlobe,
      onClick: navigation.goToWebsite,
    },
  ];
  const search = query.trim().toLowerCase();
  const visible = destinations.filter(({ title, description, group }) =>
    `${title} ${description} ${group}`.toLowerCase().includes(search)
  );

  return (
    <div className="space-y-6">
      <div ref={searchContainer}>
        <ListSearch
          label="Find a destination"
          placeholder="Search apps and destinations"
          value={query}
          onChange={setQuery}
        />
      </div>
      {visible.length === 0 ? (
        <EmptyState
          title="No destinations found"
          message="Try Customers, Services or Working hours. Unavailable features do not have destination pages."
          action={{
            label: "Show all destinations",
            variant: "outline",
            onClick: () => {
              setQuery("");
              searchContainer.current?.querySelector("input")?.focus();
            },
          }}
        />
      ) : groups.map((group) => {
        const items = visible.filter((item) => item.group === group);
        if (!items.length) return null;
        return (
          <Section key={group} title={group} count={`${items.length} destination${items.length === 1 ? "" : "s"}`}>
            <div className="space-y-2">
              {items.map(({ title, description, icon: Icon, onClick }) => (
                <InfoRow
                  key={title}
                  title={title}
                  subtitle={description}
                  icon={<IconBadge tone="primary"><Icon aria-hidden="true" /></IconBadge>}
                  searchText={query.trim()}
                  onClick={onClick}
                />
              ))}
            </div>
          </Section>
        );
      })}
      <Section title="Not available" count="No pages or actions" card>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Payments and invoices, packages, subscriptions, gift certificates,
          and contracts are not available in Froppio.
        </p>
      </Section>
      <InfoRow
        title="About"
        subtitle="Version, privacy and terms"
        icon={<IconBadge tone="primary"><LucideInfo aria-hidden="true" /></IconBadge>}
        onClick={navigation.goToAbout}
      />
    </div>
  );
}
